import { zhTW as coreZhTW } from "@mui/material/locale";
import type { Theme, ThemeOptions } from "@mui/material/styles";
import { createTheme } from "@mui/material/styles";
import { zhTW as dataGridZhTW } from "@mui/x-data-grid/locales";
// 讓 theme.components 認得 MuiDataGrid（DataGrid 的 theme 型別擴充）
import "@mui/x-data-grid/themeAugmentation";
import {
  breakpoints,
  density,
  fontFamily,
  motion,
  neutralPalette,
  shape,
  spacing,
  typeScale,
  type Md3ColorScheme,
} from "@app/design-tokens";

/**
 * 把 MD3 的色彩角色轉成 MUI 的 theme。
 *
 * 顏色與字級全部來自 @app/design-tokens，**這個檔案不自己定義任何色值**，
 * 這樣 app 端的 Paper theme 才能與 web 保持一致。
 *
 * 視覺方向是「中性優先」：
 *   - 主要動作是近黑（深色模式反過來是近白），彩色只留給語意
 *   - **不用陰影**。層次靠「白卡浮在淺灰底上 + 1px 極淺分隔線」
 *   - 圓角整體加大，按鈕與 chip 是 pill——參考 Airbnb 網站的圓角語彙：
 *     搜尋列／篩選標籤／按鈕一律 pill（`shape.full`），卡片與輸入框用大圓角
 *     （`shape.large`／`shape.medium`），彈出層（Dialog／Menu／Popover／
 *     Tooltip／Alert）也統一套用這組尺度，不留下 MUI 預設的 4px 小圓角
 *   - 表格是無框線的 list，靠 hover 底色與列間分隔線閱讀
 *
 * 注意：MUI 9 的元件解剖結構仍是 Material Design 2。這裡能對齊的是色彩、
 * 字級、圓角與間距；MD3 的 tonal/elevated 按鈕變體與 state layer 不是
 * 原生支援，需要時得自己用 sx 或 styleOverrides 補。
 */

const toMuiTypography = (key: keyof typeof typeScale) => {
  const style = typeScale[key];
  return {
    fontSize: style.fontSize,
    lineHeight: style.lineHeight / style.fontSize,
    fontWeight: style.fontWeight,
    letterSpacing: `${style.letterSpacing / style.fontSize}em`,
  };
};

function buildOptions(mode: "light" | "dark", colors: Md3ColorScheme): ThemeOptions {
  return {
    // 開啟 CSS 變數，深淺色切換不會整棵樹重新渲染
    cssVariables: { colorSchemeSelector: "data" },

    palette: {
      mode,
      primary: {
        main: colors.primary,
        contrastText: colors.onPrimary,
        light: colors.primaryContainer,
        dark: colors.onPrimaryContainer,
      },
      secondary: {
        main: colors.secondary,
        contrastText: colors.onSecondary,
        light: colors.secondaryContainer,
        dark: colors.onSecondaryContainer,
      },
      info: { main: colors.tertiary, contrastText: colors.onTertiary },
      error: { main: colors.error, contrastText: colors.onError },
      background: {
        default: colors.background,
        // 卡片是純白（深色模式是 surface），不再走 elevation 的色階
        paper: colors.surface,
      },
      text: {
        primary: colors.onSurface,
        secondary: colors.onSurfaceVariant,
      },
      divider: colors.outlineVariant,
      // MD3 的完整色彩角色，需要時用 theme.vars 或 theme.md3 取用
      ...({ md3: colors } as Record<string, unknown>),
    },

    shape: { borderRadius: shape.medium },

    spacing: spacing.base,

    breakpoints: {
      values: {
        xs: breakpoints.compact,
        sm: breakpoints.medium,
        md: breakpoints.expanded,
        lg: breakpoints.large,
        xl: breakpoints.extraLarge,
      },
    },

    typography: {
      fontFamily: fontFamily.sans,
      h1: toMuiTypography("displaySmall"),
      h2: toMuiTypography("headlineLarge"),
      h3: toMuiTypography("headlineMedium"),
      h4: toMuiTypography("headlineSmall"),
      h5: toMuiTypography("titleLarge"),
      h6: toMuiTypography("titleMedium"),
      subtitle1: toMuiTypography("titleMedium"),
      subtitle2: toMuiTypography("titleSmall"),
      body1: toMuiTypography("bodyLarge"),
      body2: toMuiTypography("bodyMedium"),
      button: { ...toMuiTypography("labelLarge"), textTransform: "none" },
      caption: toMuiTypography("bodySmall"),
      overline: { ...toMuiTypography("labelSmall"), textTransform: "uppercase" },
    },

    transitions: {
      duration: {
        shortest: motion.duration.short,
        standard: motion.duration.medium,
        complex: motion.duration.long,
      },
    },

    components: {
      /**
       * `<a>` 預設不加底線也不換色。
       *
       * 這個專案有很多「整塊都是連結」的元件（服務磚、rail 的圖示、側欄項目），
       * 它們都是 react-router 的 Link，會渲染成真正的 `<a>`。沒有這條 reset 的話
       * 卡片裡的每一行文字都會被畫上底線。
       *
       * 真正的「文字連結」一律用 `<Link>`（MuiLink），它自己會帶 hover 底線與顏色，
       * 所以不會被這條 reset 影響。
       */
      MuiCssBaseline: {
        styleOverrides: {
          a: { color: "inherit", textDecoration: "none" },
        },
      },

      // 中性設計不用陰影表達層次，所以整站關掉 Paper 的陰影
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: "none" },
          outlined: { borderColor: colors.outlineVariant },
        },
      },

      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: shape.full,
            paddingInline: 18,
            minHeight: 40,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          },
          sizeSmall: { minHeight: 32, paddingInline: 14 },
          outlined: { borderColor: colors.outline },
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: { borderRadius: shape.medium },
        },
      },

      // chip 一律 pill；狀態徽章的顏色由 statusColors 在使用處指定
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: shape.full, fontWeight: typeScale.labelMedium.fontWeight },
          filled: { backgroundColor: colors.surfaceContainer, color: colors.onSurfaceVariant },
          outlined: { borderColor: colors.outlineVariant },
        },
      },

      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: shape.large,
            backgroundColor: colors.surface,
            border: `1px solid ${colors.outlineVariant}`,
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: shape.medium,
            backgroundColor: colors.surface,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.outline },
          },
        },
      },

      MuiTextField: {
        defaultProps: { size: "small", variant: "outlined" },
      },

      MuiAppBar: {
        defaultProps: { elevation: 0, color: "transparent" },
        styleOverrides: {
          root: {
            backgroundColor: colors.surface,
            color: colors.onSurface,
            borderBottom: `1px solid ${colors.outlineVariant}`,
            backgroundImage: "none",
          },
        },
      },

      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.surface,
            borderRight: `1px solid ${colors.outlineVariant}`,
            backgroundImage: "none",
          },
        },
      },

      MuiDivider: {
        styleOverrides: { root: { borderColor: colors.outlineVariant } },
      },

      // 彈出層統一跟卡片同一組圓角尺度，避免掉回 MUI 預設的 4px
      // ——Dialog／Menu／Popover 各自的視覺份量不同，圓角也分兩級。
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: shape.large, backgroundImage: "none" },
        },
      },

      MuiMenu: {
        styleOverrides: {
          paper: { borderRadius: shape.medium, backgroundImage: "none" },
        },
      },

      MuiPopover: {
        styleOverrides: {
          paper: { borderRadius: shape.medium, backgroundImage: "none" },
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: { borderRadius: shape.small },
        },
      },

      // Snackbar 一律包 Alert（見各頁面用法），圓角在這裡統一即可
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: shape.medium },
        },
      },

      /**
       * DataGrid 改成「無框線的 list」。
       *
       * 這裡的每一條 override 都在拆掉 DataGrid 預設的方格感：
       * 拿掉外框、cell 的縱向邊框與欄位分隔線，只留下列與列之間的 1px；
       * 欄位標題變成小號的灰色標籤而不是同色粗體；hover 用底色而不是邊框。
       */
      MuiDataGrid: {
        defaultProps: {
          rowHeight: density.rowHeight,
          columnHeaderHeight: density.headerHeight,
          disableColumnMenu: false,
        },
        styleOverrides: {
          root: {
            border: "none",
            backgroundColor: "transparent",
            "--DataGrid-rowBorderColor": colors.outlineVariant,
            "--DataGrid-containerBackground": "transparent",
            "& .MuiDataGrid-columnHeaders": {
              borderBottom: `1px solid ${colors.outlineVariant}`,
            },
            "& .MuiDataGrid-columnHeader": {
              // 欄位標題是輔助資訊，不該跟內容一樣搶眼
              color: colors.onSurfaceVariant,
              fontSize: typeScale.labelMedium.fontSize,
              fontWeight: typeScale.labelMedium.fontWeight,
              letterSpacing: "0.02em",
            },
            "& .MuiDataGrid-columnSeparator": { display: "none" },
            // 沒有這一條，renderCell 放進去的區塊元素會各自對齊到不同的基線，
            // 一列裡的徽章、數字與文字看起來會上下錯開
            "& .MuiDataGrid-cell": {
              borderTop: "none",
              display: "flex",
              alignItems: "center",
            },
            "& .MuiDataGrid-row:hover": { backgroundColor: colors.surfaceContainer },
            "& .MuiDataGrid-row.Mui-selected": { backgroundColor: colors.surfaceContainer },
            // 鍵盤操作仍需要焦點提示，但滑鼠點擊不該留下方框
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
              outline: "none",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: `1px solid ${colors.outlineVariant}`,
              minHeight: 52,
            },
            "& .MuiDataGrid-overlay": { backgroundColor: "transparent" },
          },
        },
      },
    },
  };
}

/**
 * 中文化需要掛**兩份** locale：
 *   - @mui/material/locale 的 zhTW      → MuiTablePagination（一般表格的分頁列）
 *   - @mui/x-data-grid/locales 的 zhTW  → DataGrid 自己的 localeText
 *                                         （頁尾「每頁數量 / 1–7 共 7」、欄位選單、篩選、無資料）
 * DataGrid 不吃 core 的分頁字串，所以只掛一份會有一半留著英文。
 */
export const lightTheme: Theme = createTheme(
  buildOptions("light", neutralPalette.light),
  coreZhTW,
  dataGridZhTW,
);
export const darkTheme: Theme = createTheme(
  buildOptions("dark", neutralPalette.dark),
  coreZhTW,
  dataGridZhTW,
);

export type ThemeMode = "light" | "dark" | "system";
